import React, { useEffect } from "react";
import "./App.css";
import { initClickTracking, trackPageView } from "./lib/analytics";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/NeatWave/Header";
import HeroSection from "./components/NeatWave/HeroSection";
import WhyChooseSection from "./components/NeatWave/WhyChooseSection";
import BeforeAfterSection from "./components/NeatWave/BeforeAfterSection";
import ServicesSection from "./components/NeatWave/ServicesSection";
import HowItWorksSection from "./components/NeatWave/HowItWorksSection";
import AboutSection from "./components/NeatWave/AboutSection";
import CustomerReviewsSection from "./components/NeatWave/CustomerReviewsSection";
import FAQSection from "./components/NeatWave/FAQSection";
import QuoteSection from "./components/NeatWave/QuoteSection";
import CTASection from "./components/NeatWave/CTASection";
import Footer from "./components/NeatWave/Footer";
import MobilePhoneButton from "./components/NeatWave/MobilePhoneButton";
import CookieConsent from "./components/NeatWave/CookieConsent";
import PrivacyPolicy from "./components/NeatWave/PrivacyPolicy";
import { AdminWithAuth } from "./components/NeatWave/AdminDashboard";
import EndOfTenancyCleaningPage from "./components/NeatWave/EndOfTenancyCleaningPage";
import AirbnbCleaningPage from "./components/NeatWave/AirbnbCleaningPage";
import DeepCleaningPage from "./components/NeatWave/DeepCleaningPage";
import DomesticCleaningPage from "./components/NeatWave/DomesticCleaningPage";
import CommercialCleaningPage from "./components/NeatWave/CommercialCleaningPage";
import OvenCleaningPage from "./components/NeatWave/OvenCleaningPage";
import LaundryIroningPage from "./components/NeatWave/LaundryIroningPage";
import CarpetCleaningPage from "./components/NeatWave/CarpetCleaningPage";
import LandlordCleaningPage from "./components/NeatWave/LandlordCleaningPage";
import AboutPage from "./components/NeatWave/AboutPage";
import AITipsPage from "./components/NeatWave/AITipsPage";
import AIChatWidget from "./components/NeatWave/AIChatWidget";
import HomeIntroSection from "./components/NeatWave/HomeIntroSection";
import OfficeCleaningPage from "./components/NeatWave/OfficeCleaningPage";
import { BarryCleaningPage, PenarthCleaningPage } from "./components/NeatWave/LocationPages";
import { navData, faqData } from "./data/mockData";
import { usePageMeta, faqSchema } from "./lib/seo";

const ConditionalAIChatWidget = () => {
  const location = useLocation();
  // Don't show the global chat widget on admin page (it has its own AI assistant)
  if (location.pathname === '/admin') return null;
  return <AIChatWidget />;
};

const HOME_META = {
  title: "Cleaning Services Cardiff | Domestic & Commercial Cleaners",
  description: "Looking for reliable cleaners in Cardiff, Barry or Penarth? NeatWave provides domestic and commercial cleaning, deep cleaning, office cleaning and end-of-tenancy services. Request a free quote.",
  schemas: [faqSchema(faqData.questions)],
};

const HomePage = () => {
  usePageMeta(HOME_META);
  const scrollToQuote = () => {
    const el = document.getElementById("quote-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white font-jakarta">
      <Header scrollToQuote={scrollToQuote} />
      <HeroSection scrollToQuote={scrollToQuote} />
      <HomeIntroSection />
      <WhyChooseSection />
      <BeforeAfterSection />
      <ServicesSection scrollToQuote={scrollToQuote} />
      <HowItWorksSection />
      <AboutSection scrollToQuote={scrollToQuote} />
      <CustomerReviewsSection />
      <FAQSection />
      <QuoteSection />
      <CTASection scrollToQuote={scrollToQuote} />
      <Footer />
      <MobilePhoneButton phoneLink={navData.phoneLink} />
      <CookieConsent />
    </div>
  );
};

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
    if (!location.hash) window.scrollTo({ top: 0 });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

initClickTracking();

function App() {
  return (
    <BrowserRouter>
      <RouteTracker />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/admin" element={<AdminWithAuth />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/end-of-tenancy-cleaning-cardiff" element={<EndOfTenancyCleaningPage />} />
        <Route path="/airbnb-cleaning-cardiff" element={<AirbnbCleaningPage />} />
        <Route path="/deep-cleaning-cardiff" element={<DeepCleaningPage />} />
        <Route path="/domestic-cleaning-cardiff" element={<DomesticCleaningPage />} />
        <Route path="/commercial-cleaning-cardiff" element={<CommercialCleaningPage />} />
        <Route path="/office-cleaning-cardiff" element={<OfficeCleaningPage />} />
        <Route path="/cleaning-services-barry" element={<BarryCleaningPage />} />
        <Route path="/cleaning-services-penarth" element={<PenarthCleaningPage />} />
        <Route path="/oven-cleaning-cardiff" element={<OvenCleaningPage />} />
        <Route path="/laundry-ironing-cardiff" element={<LaundryIroningPage />} />
        <Route path="/carpet-cleaning-cardiff" element={<CarpetCleaningPage />} />
        <Route path="/landlord-letting-agent-cleaning-cardiff" element={<LandlordCleaningPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/ai-cleaning-tips" element={<AITipsPage />} />
      </Routes>
      {/* Global AI Chat Widget - appears on all pages except admin */}
      <ConditionalAIChatWidget />
    </BrowserRouter>
  );
}

export default App;
